'use client';

import { useContext } from 'react';
import PropTypes from 'prop-types';
import useSWR from 'swr';

import AccordionItem from '../AccordionItem';
import AccordionFade from '../AccordionFade';
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';
import DataErrorItem from '../DataErrorItem/DataErrorItem';

import { fetchFenixEditorialContent } from '../../clients/fenixClient';
import { CountryContext } from '../../utils/country-provider';
import { t } from '../../i18n';

const fetcher = ([isoCode, contentType]) =>
    fetchFenixEditorialContent(isoCode, contentType);

const FenixExpandableTextValue = ({
    title = '',
    editorialContentType = 'OVERVIEW',
    layout = 'small',
    expandedByDefault = true,
}) => {
    const countryMeta = useContext(CountryContext);

    const swrKey = countryMeta?.code
        ? [countryMeta.code, editorialContentType]
        : null;

    const { data, error, isLoading } = useSWR(swrKey, fetcher);

    const renderContent = () => {
        if (isLoading) return <SkeletonLoader />;
        if (error || (data && !data.ok)) {
            return (
                <DataErrorItem description={t('general.errorFetchingData')} />
            );
        }
        if (!data?.html) return null;
        return <div dangerouslySetInnerHTML={{ __html: data.html }} />;
    };

    if (layout === 'column') {
        return (
            <AccordionFade
                title={title}
                content={renderContent()}
                description={data?.publishedAt || ''}
                open={expandedByDefault}
            />
        );
    }

    if (layout === 'small-column-blue') {
        const lowerCaseTitle = title ? title.toLowerCase() : '';

        return (
            <AccordionItem
                blackHeaderBackground={true}
                title={`${t('general.read')} ${lowerCaseTitle}`}
                titleOpen={`${t('general.close')} ${lowerCaseTitle}`}
                childrenTitle={title}
                description={''}
                open={expandedByDefault}
                content={data?.html || undefined}>
                {!data?.html && renderContent()}
            </AccordionItem>
        );
    }

    return (
        <AccordionItem
            size={layout}
            title={title}
            open={expandedByDefault}
            content={data?.html || undefined}>
            {!data?.html && renderContent()}
        </AccordionItem>
    );
};

FenixExpandableTextValue.propTypes = {
    title: PropTypes.string,
    editorialContentType: PropTypes.oneOf([
        'OVERVIEW',
        'HISTORY',
        'ECONOMY',
        'POLITICS',
        'HOLDINGS',
    ]),
    layout: PropTypes.oneOf(['small', 'small-column-blue', 'large', 'column']),
    expandedByDefault: PropTypes.bool,
};

export default FenixExpandableTextValue;
